/**
 * UnifiedInboxMessageItem
 *
 * A single inbox message item for the unified inbox view.
 * Supports both platform messages and application messages.
 */

import {
  TicketIcon,
  ChatBubbleLeftIcon,
  ArrowPathIcon,
  UserPlusIcon,
  ExclamationTriangleIcon,
  MegaphoneIcon,
  EnvelopeIcon,
  ArchiveBoxIcon,
  CheckIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { UnifiedInboxItem } from '../../../types/app-message.types';
import { SourceBadge } from './SourceBadge';
import type { UnifiedInboxView } from '../hooks/useUnifiedInbox';

interface UnifiedInboxMessageItemProps {
  item: UnifiedInboxItem;
  view?: UnifiedInboxView;
  onRead?: (item: UnifiedInboxItem) => void;
  onArchive?: (item: UnifiedInboxItem) => void;
  onUnarchive?: (item: UnifiedInboxItem) => void;
  onDelete?: (item: UnifiedInboxItem) => void;
  onRestore?: (item: UnifiedInboxItem) => void;
  onPermanentDelete?: (item: UnifiedInboxItem) => void;
  onClick?: (item: UnifiedInboxItem) => void;
  showActions?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (item: UnifiedInboxItem) => void;
  showCheckbox?: boolean;
}

// Icon map for platform message types
const typeIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  TICKET_CREATED: TicketIcon,
  TICKET_REPLIED: ChatBubbleLeftIcon,
  TICKET_STATUS_CHANGED: ArrowPathIcon,
  TICKET_ASSIGNED: UserPlusIcon,
  SLA_BREACH: ExclamationTriangleIcon,
  ANNOUNCEMENT: MegaphoneIcon,
  DIRECT_MESSAGE: EnvelopeIcon,
};

// Color map for platform message types
const typeColorMap: Record<string, string> = {
  TICKET_CREATED: 'bg-blue-100 text-blue-600',
  TICKET_REPLIED: 'bg-green-100 text-green-600',
  TICKET_STATUS_CHANGED: 'bg-yellow-100 text-yellow-600',
  TICKET_ASSIGNED: 'bg-purple-100 text-purple-600',
  SLA_BREACH: 'bg-red-100 text-red-600',
  ANNOUNCEMENT: 'bg-orange-100 text-orange-600',
  DIRECT_MESSAGE: 'bg-gray-100 text-gray-600',
};

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function UnifiedInboxMessageItem({
  item,
  view = 'inbox',
  onRead,
  onArchive,
  onUnarchive,
  onDelete,
  onRestore,
  onPermanentDelete,
  onClick,
  showActions = true,
  isSelected = false,
  onToggleSelect,
  showCheckbox = false,
}: UnifiedInboxMessageItemProps) {
  // For app messages, use a generic icon
  // For platform messages, use the type-specific icon
  const Icon = item.source === 'application'
    ? EnvelopeIcon
    : (typeIconMap[item.sender.type] || EnvelopeIcon);

  const iconColor = item.source === 'application'
    ? 'bg-blue-100 text-blue-600'
    : (typeColorMap[item.sender.type] || 'bg-gray-100 text-gray-600');

  const handleClick = () => {
    if (!item.isRead && onRead) {
      onRead(item);
    }
    if (onClick) {
      onClick(item);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={clsx(
        'flex gap-3 p-3 rounded-lg transition-colors cursor-pointer',
        isSelected && 'bg-indigo-50 hover:bg-indigo-100',
        !isSelected && !item.isRead && 'bg-blue-50 hover:bg-blue-100',
        !isSelected && item.isRead && 'bg-white hover:bg-gray-50'
      )}
    >
      {/* Selection Checkbox */}
      {showCheckbox && (
        <div className="flex-shrink-0 self-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelect?.(item);
            }}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 rounded text-primary-600 focus:ring-primary-500 cursor-pointer"
            style={{ borderColor: 'var(--color-border)' }}
          />
        </div>
      )}

      {/* Icon */}
      <div className={clsx('flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center', iconColor)}>
        <Icon className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p
              className={clsx(
                'text-sm truncate',
                !item.isRead && 'font-semibold text-gray-900',
                item.isRead && 'font-medium text-gray-700'
              )}
            >
              {item.subject}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-xs text-gray-500 truncate">
                {item.sender.name}
              </p>
              <SourceBadge
                source={item.source}
                applicationName={item.sender.name}
                applicationCode={item.sender.applicationCode}
              />
            </div>
          </div>
          <span className="text-xs text-gray-400 flex-shrink-0">
            {formatRelativeTime(item.createdAt)}
          </span>
        </div>

        <p className="text-xs text-gray-500 line-clamp-2 mt-1">{item.body}</p>

        {/* Actions */}
        {showActions && (
          <div className="flex gap-3 mt-2">
            {/* Inbox view actions */}
            {view === 'inbox' && (
              <>
                {!item.isRead && onRead && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRead(item);
                    }}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <CheckIcon className="h-3.5 w-3.5" />
                    Mark read
                  </button>
                )}
                {onArchive && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onArchive(item);
                    }}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                  >
                    <ArchiveBoxIcon className="h-3.5 w-3.5" />
                    Archive
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(item);
                    }}
                    className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                    Delete
                  </button>
                )}
              </>
            )}

            {/* Archived view actions */}
            {view === 'archived' && (
              <>
                {onUnarchive && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onUnarchive(item);
                    }}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                    Move to Inbox
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(item);
                    }}
                    className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                    Delete
                  </button>
                )}
              </>
            )}

            {/* Deleted view actions */}
            {view === 'deleted' && (
              <>
                {onRestore && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRestore(item);
                    }}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                    Restore
                  </button>
                )}
                {onPermanentDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onPermanentDelete(item);
                    }}
                    className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                    Delete Forever
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Unread indicator */}
      {!item.isRead && (
        <div className="flex-shrink-0 self-center">
          <div className="h-2 w-2 rounded-full bg-blue-500" />
        </div>
      )}
    </div>
  );
}

export default UnifiedInboxMessageItem;
